import type { FC, ReactNode } from 'react'

import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react'
import { createPortal } from 'react-dom'
import {
    Camera,
    CameraOff,
    Images,
    LoaderCircle,
    RefreshCw,
    SwitchCamera,
    X
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/kit/button'
import { videoFrameToPhoto } from '@/lib/carwash/image'

type CameraError = 'denied' | 'unsupported' | 'missing' | 'unknown'

const ERROR_COPY: Record<CameraError, { title: string, description: string }> = {
    denied: {
        title: 'Camera access is blocked',
        description: 'Allow camera access for this site in your browser settings, or take the photo with your phone camera instead.'
    },
    unsupported: {
        title: 'Live camera is not available here',
        description: 'Take the photo with your phone camera, or choose one from the gallery.'
    },
    missing: {
        title: 'No camera found',
        description: 'This device has no camera we can use. Choose a photo from the gallery instead.'
    },
    unknown: {
        title: 'The camera could not start',
        description: 'Another app may be using it. Try again, or use your phone camera instead.'
    }
}

interface CameraCaptureProps {
    open: boolean
    onClose: () => void
    onCapture: (dataUrl: string) => void
    onUseDeviceCamera: () => void
    onUseGallery: () => void
}

/*
 * A full-screen viewfinder using the device camera. Permission is only
 * requested when this opens. If the browser refuses, it offers the phone's
 * own camera app or the gallery instead, so a photo can always be added.
 */
export const CameraCapture: FC<CameraCaptureProps> = ({
    open,
    onClose,
    onCapture,
    onUseDeviceCamera,
    onUseGallery
}): ReactNode => {
    const videoRef = useRef<HTMLVideoElement>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const shutterRef = useRef<HTMLButtonElement>(null)

    const [status, setStatus] = useState<'starting' | 'live' | 'error'>('starting')
    const [error, setError] = useState<CameraError>('unknown')
    const [facing, setFacing] = useState<'environment' | 'user'>('environment')
    const [canSwitch, setCanSwitch] = useState(false)
    const [flash, setFlash] = useState(false)
    const [attempt, setAttempt] = useState(0)

    const stopStream = useCallback(() => {
        streamRef.current?.getTracks().forEach(track => track.stop())
        streamRef.current = null
    }, [])

    useEffect(() => {
        if (!open) {
            stopStream()
            return
        }

        let cancelled = false

        const start = async () => {
            setStatus('starting')

            if (!navigator.mediaDevices?.getUserMedia) {
                setError('unsupported')
                setStatus('error')
                return
            }

            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: false,
                    video: {
                        facingMode: { ideal: facing },
                        width: { ideal: 1920 },
                        height: { ideal: 1080 }
                    }
                })

                if (cancelled) {
                    stream.getTracks().forEach(track => track.stop())
                    return
                }

                stopStream()
                streamRef.current = stream

                const video = videoRef.current

                if (video) {
                    video.srcObject = stream
                    await video.play().catch(() => undefined)
                }

                setStatus('live')

                const devices = await navigator.mediaDevices.enumerateDevices().catch(() => [])
                if (!cancelled) setCanSwitch(devices.filter(device => device.kind === 'videoinput').length > 1)
            }
            catch (cause) {
                if (cancelled) return

                const name = cause instanceof DOMException ? cause.name : ''

                if (name === 'NotAllowedError' || name === 'SecurityError') setError('denied')
                else if (name === 'NotFoundError' || name === 'OverconstrainedError') setError('missing')
                else setError('unknown')

                setStatus('error')
            }
        }

        void start()

        return () => {
            cancelled = true
            stopStream()
        }
    }, [open, facing, attempt, stopStream])

    const onCloseRef = useRef(onClose)

    useEffect(() => {
        onCloseRef.current = onClose
    }, [onClose])

    useEffect(() => {
        if (!open) return

        const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null
        document.body.style.overflow = 'hidden'

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onCloseRef.current()
        }

        window.addEventListener('keydown', onKeyDown)
        const focusTimer = window.setTimeout(() => shutterRef.current?.focus(), 50)

        return () => {
            window.clearTimeout(focusTimer)
            window.removeEventListener('keydown', onKeyDown)
            document.body.style.overflow = ''
            previous?.focus({ preventScroll: true })
        }
    }, [open])

    const capture = () => {
        const video = videoRef.current
        if (!video || status !== 'live') return

        try {
            const photo = videoFrameToPhoto(video)

            setFlash(true)
            window.setTimeout(() => setFlash(false), 450)

            onCapture(photo)
        }
        catch {
            setError('unknown')
            setStatus('error')
        }
    }

    if (!open) return null

    const copy = ERROR_COPY[error]

    return createPortal(
        <div
            role='dialog'
            aria-modal='true'
            aria-label='Take a vehicle photo'
            className='fixed inset-0 z-[60] flex flex-col bg-black text-white'
        >
            <video
                ref={videoRef}
                playsInline
                muted
                autoPlay
                className={cn(
                    'absolute inset-0 size-full object-cover transition-opacity duration-300',
                    status === 'live' ? 'opacity-100' : 'opacity-0',
                    facing === 'user' && '-scale-x-100'
                )}
            />

            {flash && <div className='pointer-events-none absolute inset-0 bg-white animate-flash' aria-hidden='true' />}

            <div className='relative z-10 flex items-center justify-between px-3 pt-[calc(0.75rem+env(safe-area-inset-top))] pb-3'>
                <button
                    type='button'
                    onClick={onClose}
                    aria-label='Close camera'
                    className='grid size-11 place-items-center rounded-full bg-black/40 backdrop-blur focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none'
                >
                    <X className='size-6' aria-hidden='true' />
                </button>

                <p className='rounded-full bg-black/40 px-3 py-1.5 text-sm font-semibold backdrop-blur'>
                    Vehicle photo
                </p>

                {canSwitch && status === 'live'
                    ? (
                        <button
                            type='button'
                            onClick={() => setFacing(value => value === 'environment' ? 'user' : 'environment')}
                            aria-label='Switch camera'
                            className='grid size-11 place-items-center rounded-full bg-black/40 backdrop-blur focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none'
                        >
                            <SwitchCamera className='size-6' aria-hidden='true' />
                        </button>
                    )
                    : <span className='size-11' aria-hidden='true' />}
            </div>

            <div className='relative z-10 flex flex-1 items-center justify-center px-6'>
                {status === 'live' && (
                    <div className='pointer-events-none relative aspect-[4/3] w-full max-w-xl' aria-hidden='true'>
                        <span className='absolute top-0 left-0 size-8 rounded-tl-2xl border-t-4 border-l-4 border-white/90' />
                        <span className='absolute top-0 right-0 size-8 rounded-tr-2xl border-t-4 border-r-4 border-white/90' />
                        <span className='absolute bottom-0 left-0 size-8 rounded-bl-2xl border-b-4 border-l-4 border-white/90' />
                        <span className='absolute right-0 bottom-0 size-8 rounded-br-2xl border-r-4 border-b-4 border-white/90' />
                        <p className='absolute inset-x-0 -bottom-9 text-center text-sm font-medium text-white/90 drop-shadow'>
                            Keep the number plate in view
                        </p>
                    </div>
                )}

                {status === 'starting' && (
                    <div className='flex flex-col items-center gap-3 text-white/80'>
                        <LoaderCircle className='size-8 animate-spin' aria-hidden='true' />
                        <p className='text-sm font-medium'>
                            Starting camera
                        </p>
                    </div>
                )}

                {status === 'error' && (
                    <div className='w-full max-w-sm rounded-2xl bg-white p-5 text-center text-foreground'>
                        <span className='mx-auto grid size-12 place-items-center rounded-full bg-slate-100 text-slate-600'>
                            <CameraOff className='size-6' aria-hidden='true' />
                        </span>

                        <h2 className='mt-3 font-heading text-lg font-bold'>
                            {copy.title}
                        </h2>

                        <p className='mt-1 text-sm leading-relaxed text-muted-foreground'>
                            {copy.description}
                        </p>

                        <div className='mt-5 grid gap-2'>
                            {error !== 'missing' && (
                                <Button size='lg' onClick={onUseDeviceCamera}>
                                    <Camera aria-hidden='true' />
                                    Use phone camera
                                </Button>
                            )}

                            <Button size='lg' variant='secondary' onClick={onUseGallery}>
                                <Images aria-hidden='true' />
                                Choose from gallery
                            </Button>

                            {(error === 'unknown' || error === 'denied') && (
                                <Button size='lg' variant='ghost' onClick={() => setAttempt(value => value + 1)}>
                                    <RefreshCw aria-hidden='true' />
                                    Try again
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className='relative z-10 grid grid-cols-3 items-center px-6 pt-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))]'>
                <button
                    type='button'
                    onClick={onUseGallery}
                    className='flex flex-col items-center gap-1 justify-self-start text-xs font-medium text-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-lg p-1'
                >
                    <span className='grid size-12 place-items-center rounded-xl bg-white/15 backdrop-blur'>
                        <Images className='size-6' aria-hidden='true' />
                    </span>
                    Gallery
                </button>

                <button
                    ref={shutterRef}
                    type='button'
                    onClick={capture}
                    disabled={status !== 'live'}
                    aria-label='Take photo'
                    className='grid size-[76px] place-items-center justify-self-center rounded-full border-4 border-white transition-transform active:scale-95 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-sky-400'
                >
                    <span className='size-[60px] rounded-full bg-white' />
                </button>

                <span aria-hidden='true' />
            </div>
        </div>,
        document.body
    )
}
